import { SOCKET_CLIENT_VERSION, SOCKET_CLOSE_KICKED, SOCKET_CLOSE_MISSING_AUTH, SOCKET_CLOSE_REQUIRE_TURNSTILE_TOKEN, SOCKET_CLOSE_SERVER_TOO_SLOW } from '@colonist/types';
import { UIUtilsEjsVariables } from '../UIUtilsEjsVariables.ts';
import { logDebugInfo, logError } from '../ui_utils/Utils.ts';
import * as MsgPack from '@msgpack/msgpack';
import { StorageWrapper } from '../StorageWrapper.ts';
import { getHomepageUrl, navigateToPage } from '../ui_utils/ClientUtils.ts';
import { createDeferredPromise, TimeUtils } from '@colonist/utils';
import { UITurnstile } from '../network/UITurnstile.ts';
export class SocketClient {
    isSocketOpen() {
        var _this_webSocket;
        return ((_this_webSocket = this.webSocket) === null || _this_webSocket === void 0 ? void 0 : _this_webSocket.readyState) === WebSocket.OPEN;
    }
    async connect() {
        const resultPromise = createDeferredPromise();
        const connectTimeout = 9 * TimeUtils.MILLISECONDS;
        setTimeout(()=>{
            if (!resultPromise.pending) return;
            this.close(SOCKET_CLOSE_SERVER_TOO_SLOW);
            resultPromise.resolve('retry');
        }, connectTimeout);
        this.webSocket = new WebSocket(await this.getWebSocketUrl());
        this.webSocket.binaryType = 'arraybuffer';
        this.webSocket.onmessage = (message)=>this.handleInitialWebSocketMessage(message, resultPromise);
        this.webSocket.onclose = (ev)=>{
            switch(ev.code){
                case SOCKET_CLOSE_KICKED:
                case SOCKET_CLOSE_MISSING_AUTH:
                    resultPromise.resolve('permanentlyFailed');
                    break;
                case SOCKET_CLOSE_REQUIRE_TURNSTILE_TOKEN:
                    this.sendTurnstileToken = true;
                    resultPromise.resolve('retry');
                    break;
                default:
                    resultPromise.resolve('retry');
                    break;
            }
            this.handleWebSocketClose(ev.code);
        };
        this.webSocket.onerror = (error)=>{
            this.handleWebSocketError(error);
            resultPromise.resolve('retry');
        };
        return resultPromise;
    }
    async getWebSocketUrl() {
        const queryString = new URLSearchParams();
        queryString.append('version', SOCKET_CLIENT_VERSION);
        if (this.firstUserSessionId != undefined) {
            queryString.append('firstUserSessionId', this.firstUserSessionId);
        }
        if (UIUtilsEjsVariables.isUserOnDiscordMobile) {
            const authToken = StorageWrapper.getAuthToken();
            if (authToken != null) queryString.append('authToken', authToken);
        }
        if (this.sendTurnstileToken) {
            queryString.append('turnstileToken', await UITurnstile.getToken());
        }
        return `${this.serverUrl}?${queryString}`;
    }
    handleInitialWebSocketMessage(message, connectionPromise) {
        if (this.webSocket == undefined) return; // TS only
        try {
            const socketEvent = JSON.parse(message.data);
            switch(socketEvent.type){
                case 'Connected':
                    if (this.firstUserSessionId == undefined) {
                        this.firstUserSessionId = socketEvent.userSessionId;
                    }
                    return;
                case 'SessionEstablished':
                    this.webSocket.onmessage = (normalMessage)=>this.handleWebSocketMessage(normalMessage);
                    this.onSessionEstablished();
                    connectionPromise.resolve('connected');
                    this.sendTurnstileToken = false;
                    return;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Parse websocket initial message error', message, error);
        }
        this.handleWebSocketMessage(message);
    }
    handleWebSocketMessage(message) {
        const dataObject = MsgPack.decode(message.data);
        if (UIUtilsEjsVariables.socketDebugActive) {
            logDebugInfo('SocketMessage', [
                '======',
                `Message type: ${dataObject.id}, data:`,
                dataObject.data
            ]);
        }
        this.onStateUpdate(Number(dataObject.id), dataObject.data);
    }
    handleWebSocketError(error) {
        var _this_webSocket;
        if (UIUtilsEjsVariables.socketDebugActive) logError('connect', [
            'Closing socket, encountered error: ',
            error
        ]);
        (_this_webSocket = this.webSocket) === null || _this_webSocket === void 0 ? void 0 : _this_webSocket.close();
    }
    handleWebSocketClose(code) {
        if (code == SOCKET_CLOSE_KICKED) {
            navigateToPage(getHomepageUrl());
            return;
        }
        this.onClose();
    }
    close(code) {
        var _this_webSocket;
        (_this_webSocket = this.webSocket) === null || _this_webSocket === void 0 ? void 0 : _this_webSocket.close(code);
    }
    send(binarySocketMessage) {
        var _this_webSocket;
        if (!this.isSocketOpen()) return;
        (_this_webSocket = this.webSocket) === null || _this_webSocket === void 0 ? void 0 : _this_webSocket.send(binarySocketMessage);
    }
    constructor(serverUrl, onSessionEstablished, onStateUpdate, onClose){
        this.sendTurnstileToken = false;
        this.serverUrl = serverUrl;
        this.onSessionEstablished = onSessionEstablished;
        this.onStateUpdate = onStateUpdate;
        this.onClose = onClose;
    }
}
